/* eslint-disable prefer-template */
import * as API from 'es6/core/api';
import Config from 'es6/core/config';
import Logger from 'es6/services/logger';
import { checkDiscardData } from 'es6/controllers/apps/debug-overrides';

const LOCAL_STORE_NAME = 'stashed_presentation_data';

/**
 * @param {Function} onLoadStart
 * @param {Function} onLoaded
 * @param {Function} onError
 */
export function checkForStoredPresentationData(onLoadStart, onLoaded, onError) {
  let stashedPresentationData = window.localStorage.getItem(LOCAL_STORE_NAME);

  if (stashedPresentationData) {
    stashedPresentationData = JSON.parse(stashedPresentationData);

    if (stashedPresentationData.username === Config.user.username) {
      onLoadStart();

      // Retry the upload. We only want to delete the data if the server
      // returns a response.
      uploadPresentationData(stashedPresentationData.presentations, stashedPresentationData.applicationType)
        .then(response => {
          // Remove data we just reuploaded from localstorage.
          window.localStorage.removeItem(LOCAL_STORE_NAME);

          // Trigger onLoaded callback.
          onLoaded();
        })
        .catch(reason => {
          // Only remove the presentations from storage if it is not related to
          // a network error.
          if (!(reason.name === 'AjaxTimeoutError' || reason.name === 'NetworkError')) {
            window.localStorage.removeItem(LOCAL_STORE_NAME);
          }

          // If the error code is a 409, that means the data was
          // actually sent successfully on a previous call.
          if (reason.name === 'AjaxError' && reason.code === 409) {
            onLoaded();
          } else {
            onError(reason);
          }
        });
    } else {
      // Logged in as a different user than whoever the stashed data belongs to,
      // so drop it and carry on.
      window.localStorage.removeItem(LOCAL_STORE_NAME);
      onLoaded();
    }
  } else {
    // Nothing to upload.
    onLoaded();
  }
}

export function storePresentationData(presentationData, applicationType) {
  const stringifiedPresentationData = JSON.stringify({
    presentations:   presentationData,
    username:        Config.user.username,
    applicationType,
  });
  window.localStorage.setItem(LOCAL_STORE_NAME, stringifiedPresentationData);
}

export function uploadPresentationData(presentationData, applicationType) {
  // Debugging option to prevent data upload.
  if (checkDiscardData()) {
    return Promise.resolve({});
  }

  // Nothing to upload.
  if (presentationData.length === 0) {
    return Promise.resolve({});
  }

  const params  = {
    presentations:    presentationData,
    applicationType,
  };
  const saver = API.postStudyData(params);

  saver.catch(function (error) {
    // A 409 means that the data was already received, but anything
    // in the 400 range means the data is bad. As long as the error
    // does not fall within this range, save the presentation data
    // for reupload later. Unless it just a TokenExpiredError where
    // reloading the app will fix the data.
    if (error.code < 400 || error.code >= 500 || error.name === 'AjaxTimeoutError' || error.name === 'NetworkError' || error.name === 'TokenExpiredError') {
      Logger.warn('StudyDataManager', 'Failed to send presentationData, saving presentations locally.');
      storePresentationData(presentationData, applicationType);
    } else {
      Logger.error('StudyDataManager', 'Failed to send presentationData with error code ' + error.code + ', discarding presentation data.');
    }
  });
  return saver;
}
